import React, { useState } from "react";
import { useParams } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
    faArrowDown,
    faArrowUp,
    faExchangeAlt,
    faInfoCircle,
    faUser
} from "@fortawesome/free-solid-svg-icons";
import { ColumnDef } from "@tanstack/react-table";
import {
    getCoreRowModel,
    getPaginationRowModel,
    useReactTable,
    PaginationState,
} from "@tanstack/react-table";

import AdvanceTable from "components/base/AdvanceTable";
import AdvanceTableFooter from "components/base/AdvanceTableFooter";
import AdvanceTableProvider from "providers/AdvanceTableProvider";
import Badge from "components/base/Badge";
import { useExtrato, useConta } from "hooks/useContas";
import { useBreakpoints } from "providers/BreakpointsProvider";
import { MovimentoExtratoType } from "services/contaService";

// Funções auxiliares
const formatarDataHora = (data: string) => {
    return new Date(data).toLocaleString('pt-BR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
    });
};

const formatarMoeda = (valor: number) => {
    return new Intl.NumberFormat('pt-BR', {
        style: 'currency',
        currency: 'BRL'
    }).format(valor);
};

const obterIconeMovimento = (tipo: number) => {
    switch (tipo) {
        case 0: return faArrowDown;
        case 1: return faArrowUp;
        case 2: return faExchangeAlt;
        default: return faInfoCircle;
    }
};

const obterTipoDescricao = (tipo: number) => {
    switch (tipo) {
        case 0: return 'Depósito';
        case 1: return 'Saque';
        case 2: return 'Transferência';
        default: return 'Movimento';
    }
};

// Definir as colunas da tabela
const createExtratoColumns = (isMobile: boolean, conta: any): ColumnDef<MovimentoExtratoType>[] => [
    {
        accessorKey: "data",
        header: "Data",
        cell: ({ row: { original } }) => (
            <span className="fs-9 text-body-secondary">
                {formatarDataHora(original.data)}
            </span>
        ),
        meta: {
            headerProps: { style: { width: 140 }, className: "ps-4" },
            cellProps: { className: "ps-4" },
        },
    },
    {
        accessorKey: "tipo",
        header: "Tipo",
        cell: ({ row: { original } }) => (
            <div className="d-flex align-items-center">
                <FontAwesomeIcon
                    icon={obterIconeMovimento(original.tipo as number)}
                    className="me-2"
                />
                <Badge variant="phoenix" bg={
                    original.tipo === 0 ? 'success' :
                        original.tipo === 1 ? 'danger' : 'info'
                }>
                    {obterTipoDescricao(original.tipo as number)}
                </Badge>
            </div>
        ),
        meta: {
            headerProps: { style: { width: 140 }, className: "ps-4" },
            cellProps: { className: "ps-4" },
        },
    },
    {
        accessorKey: "descricao",
        header: "Descrição",
        cell: ({ row: { original } }) => {
            const isCredito = original.tipo === 0 ||
                (original.tipo === 2 && original.contaDestinoNome === conta?.nome);

            return (
                <div>
                    <div className="fw-semibold text-body-emphasis">{original.descricao}</div>
                    {original.tipo === 2 && (
                        <small className="text-body-secondary">
                            {isCredito ?
                                `De: ${original.contaOrigemNome}` :
                                `Para: ${original.contaDestinoNome}`
                            }
                        </small>
                    )}
                </div>
            );
        },
        meta: {
            headerProps: { style: { width: 300 }, className: "ps-4" },
            cellProps: { className: "ps-4" },
        },
    },
    ...(isMobile ? [] : [{
        accessorKey: "usuarioNome" as keyof MovimentoExtratoType,
        header: "Usuário",
        cell: ({ row: { original } }: { row: { original: MovimentoExtratoType } }) => (
            <span className="fs-9 text-body-secondary">
                <FontAwesomeIcon icon={faUser} className="me-1" />
                {original.usuarioNome || 'Sistema'}
            </span>
        ),
        meta: {
            headerProps: { style: { width: 120 }, className: "ps-4" },
            cellProps: { className: "ps-4" },
        },
    }]),
    {
        accessorKey: "valor",
        header: "Valor",
        cell: ({ row: { original } }) => {
            const isCredito = original.tipo === 0 ||
                (original.tipo === 2 && original.contaDestinoNome === conta?.nome);

            return (
                <span className={`fw-bold ${isCredito ? 'text-success-emphasis' : 'text-danger-emphasis'}`}>
                    {isCredito ? '+' : '-'}{formatarMoeda(original.valor)}
                </span>
            );
        },
        meta: {
            headerProps: { style: { width: 120 }, className: "text-end" },
            cellProps: { className: "text-end" },
        },
    },
    {
        accessorKey: "saldoAposMovimento",
        header: "Saldo",
        cell: ({ row: { original } }) => (
            <span className={`fw-bold ${original.saldoAposMovimento >= 0 ? 'text-success-emphasis' : 'text-danger-emphasis'}`}>
                {formatarMoeda(original.saldoAposMovimento)}
            </span>
        ),
        meta: {
            headerProps: { style: { width: 120 }, className: "text-end" },
            cellProps: { className: "text-end" },
        },
    },
];

const ExtratoTable: React.FC = () => {
    const { id } = useParams();
    const contaId = (id || "0");
    const { breakpoints } = useBreakpoints();
    const isMobile = breakpoints.down('md');

    // Estado para paginação
    const [pagination, setPagination] = useState<PaginationState>({
        pageIndex: 0,
        pageSize: 20,
    });

    // Queries com keepPreviousData
    const extratoQuery = useExtrato(contaId, pagination.pageSize, pagination.pageIndex);
    const contaQuery = useConta(contaId);

    const tableData = extratoQuery.data?.data || [];
    const conta = contaQuery.data;
    const totalMovimentos = extratoQuery.data?.total || 0;

    // Configurar tabela
    const columns = createExtratoColumns(isMobile, conta);

    const table = useReactTable({
        data: tableData,
        columns,
        getCoreRowModel: getCoreRowModel(),
        getPaginationRowModel: getPaginationRowModel(),
        onPaginationChange: setPagination,
        manualPagination: true,
        pageCount: totalMovimentos ? Math.ceil(totalMovimentos / pagination.pageSize) : 0,
        state: {
            pagination,
        },
        initialState: {
            pagination: {
                pageIndex: 0,
                pageSize: 20,
            },
        },
    });

    if (extratoQuery.isLoading || contaQuery.isLoading) {
        return (
            <div className="d-flex justify-content-center align-items-center py-5">
                <div className="spinner-border" role="status">
                    <span className="visually-hidden">Carregando extrato...</span>
                </div>
            </div>
        );
    }

    if (extratoQuery.isError || contaQuery.isError) {
        return (
            <div className="alert alert-danger border border-danger border-opacity-25 bg-danger-subtle">
                <div className="text-danger-emphasis">
                    <FontAwesomeIcon icon={faInfoCircle} className="me-2" />
                    Erro ao carregar extrato. Tente novamente mais tarde.
                </div>
            </div>
        );
    }

    return (
        <div>
            {/* Saldo anterior se existirem movimentos */}
            {tableData.length > 0 && extratoQuery.data?.meta?.saldoAnterior !== undefined && (
                <div className="alert alert-info border border-info border-opacity-25 bg-info-subtle mb-3">
                    <div className="text-info-emphasis">
                        <div className="d-flex justify-content-between align-items-center">
                            <span>
                                <FontAwesomeIcon icon={faInfoCircle} className="me-2" />
                                Saldo anterior aos movimentos exibidos
                            </span>
                            <span className={`fw-bold ${Number(extratoQuery.data.meta.saldoAnterior) >= 0 ? 'text-success-emphasis' : 'text-danger-emphasis'}`}>
                                {formatarMoeda(Number(extratoQuery.data.meta.saldoAnterior))}
                            </span>
                        </div>
                    </div>
                </div>
            )}

            <AdvanceTableProvider {...table}>
                <AdvanceTable
                    tableProps={{ className: "phoenix-table fs-9" }}
                />
                <AdvanceTableFooter
                    pagination
                    showViewAllBtn={false}
                />
            </AdvanceTableProvider>
        </div>
    );
};

export default ExtratoTable; 